import {
  type IConfigCatClient,
  type IConfigCatLogger,
  type IEvaluationDetails,
  type IJSManualPollOptions,
  LogLevel,
  PollingMode,
  User,
  createConsoleLogger,
  getClient,
} from 'configcat-js'

import { portal } from '@/helpers/Environment'
import { randomIdentifier } from '@/helpers/StringHelpers'

import { logInTrackJS } from '@/errors/helpers'

export type Flags = Partial<{
  // @see https://ticketure-team.atlassian.net/browse/TIC-4802
  hide_membership_links: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-4760
  address_autocomplete_for_uk_counties: boolean

  // TODO When we turn this on for everyone and remove this flag make sure to delete StripeWalletPayment and move its
  // paymentRequestButton() method logic into a helper. Also update mockStripePaymentRequest() to stop
  // mocking methods that no longer need to be.
  ignore_wallet_identity_data: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-4691
  tg_seat_accessibility: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-6544
  auth0_session_revocation: boolean

  // @see https://ticketure-team.atlassian.net/browse/TIC-6416
  ecomm_next_donations: boolean
}>
export const configCatRequest = fetchConfigCatFlags()

/**
 * Asynchronous access to flags outside Vue components. The caller must be sure that flags have been initialized.
 *
 * Prefer `this.flags` inside Vue components, or `configCatRequest` if flags might be uninitialized.
 */
export let flags: Flags | undefined

export async function initializeFlags() {
  flags = await configCatRequest
}

async function fetchConfigCatFlags(): Promise<Flags> {
  const client = initializeClient()
  await client.forceRefreshAsync()
  const response = await client.getAllValuesAsync(getUser())

  const result = {}
  for (const { settingKey, settingValue } of response) {
    result[settingKey] = settingValue
  }
  return result
}

function initializeClient(): IConfigCatClient {
  const keys = {
    development: 'configcat-sdk-1/NN3dCGfZuEeHDMaycMa2BQ/Y0P7CPtDtkaF5Kai4NqjzA',
    production: 'configcat-sdk-1/NN3dCGfZuEeHDMaycMa2BQ/2TnswJfCKE6I21jv11CI3w',
    staging: 'configcat-sdk-1/NN3dCGfZuEeHDMaycMa2BQ/_coIeWEQQkKfmK2i4DPb1w',
  }

  // Use the strictest flag configuration as the fallback.
  const key = keys[window.tixEnv] ?? keys.production

  const options: IJSManualPollOptions = {
    logger: initializeLogger(window.tixEnv),
    setupHooks: (hooks) => hooks.on('flagEvaluated', sendFlagToAnalytics),
  }

  return getClient(key, PollingMode.ManualPoll, options)
}

/**
 * Sends ConfigCat flag evaluation data to Google Analytics.
 * @see https://configcat.com/blog/2024/09/20/ab-testing-configcat-google-analytics/
 */
function sendFlagToAnalytics(details: IEvaluationDetails): void {
  if (!details.variationId) {
    // variationId is optional in ConfigCat types, but we need it to group users by flag variant in GA4.
    logInTrackJS(`ConfigCat flag is missing variationId`, details)
  }

  window.dataLayer.push({
    event: 'configcat_flag_evaluated',
    exp_variant_string: `configcat-${details.key}-${details.value}`,
    variation_id: details.variationId ?? `${details.key}-${details.value}`,
  })
}

function initializeLogger(env: string): IConfigCatLogger | undefined {
  // Should not expose our flags and targeting rules to users.
  if (env !== 'production') {
    return createConsoleLogger(LogLevel.Warn)
  }
}

export function getUser(): User {
  // A percentage evaluation attribute is required to use percentage-based targeting. See https://configcat.com/docs/targeting/percentage-options/
  // This isn't a user ID as it does not take into account logged-in users; it's just a random device identifier to allow percentage-based targeting to work reasonably well.
  // We use `localStorage` here because we want the user to have the same UX across tabs. This id is only used locally, and for this specific purpose, VueX would be overkill.
  // `?.` is used to avoid runtime errors when localStorage is not available.
  let deviceId = localStorage?.getItem('configcat-device-id')

  if (!deviceId) {
    deviceId = randomIdentifier()
    // `localStorage` isn't available in some scenarios, particularly in android webviews. See https://ticketure-team.atlassian.net/browse/TIC-4723. In those cases we don't attempt to sync device id across tabs.
    localStorage?.setItem('configcat-device-id', deviceId)
  }

  // The 'placeholder' prevents warnings for flags that filter users by email address.
  return new User('', 'placeholder', '', { tenant: portal.slug, tags: [], random_device_id: deviceId })
}
